import { useState, useCallback } from 'react';
import axios from 'axios';
import { enhancedApi } from '@/shared/services/enhancedApi';
import { usePlanSelection } from '../contexts/PlanSelectionContext';
import { calculateAnnualMaxCoverageDate } from '@/utils/dateCalculations';
import { useAppStore } from '@/store/useAppStore';
import { useLocation } from "react-router-dom";
import { getUtmQsParams } from '@/shared/utils/utmUtils';

/**
 * Format date string to yyyy-mm-dd format for API requests
 * Handles various input formats and ensures consistent output
 */
const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  
  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Try to parse and convert to YYYY-MM-DD using local date components
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn('usePrincipalPolicyValidation: Invalid date format:', dateString);
    return dateString; // Return original if can't parse
  }
  
  // Use local date components instead of UTC to avoid timezone shifts
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Request interface for principal policy validation
interface PrincipalPolicyValidationRequest {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  birthdate: string;
  gender: string;
  idType: string;
  idNumber: string;
  coverage_start: string;
  coverage_end: string;
  dataId: string;
}

// Response interface for principal policy validation
interface PrincipalPolicyValidationResponse {
  success: boolean;
  coverage_end: string | null;
  coverage_start: string | null;
  message: string;
  code?: number;
  data?: unknown;
}

// Principal insured data interface (matching the component's interface)
interface PrincipalInsuredData {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  dateOfBirth: string;
  gender: string;
  idType: string;
  idNumber: string;
}

interface UsePrincipalPolicyValidationReturn {
  validatePrincipalPolicy: (principalData: PrincipalInsuredData) => Promise<PrincipalPolicyValidationResponse>;
  loading: boolean;
  error: string | null;
  clearState: () => void;
}

export const usePrincipalPolicyValidation = (): UsePrincipalPolicyValidationReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Access tempData from PlanSelectionContext to get travel information
  const { getTempDataValue, tempData } = usePlanSelection();
  const { tempDataId } = useAppStore();

  // Helper function to calculate coverage dates based on trip type
  // Updated to match SummaryStep pattern for consistent data access
  const calculateCoverageDates = useCallback(() => {
    console.log('🔍 usePrincipalPolicyValidation: Starting calculateCoverageDates...');
    
    // Use the same pattern as SummaryStep for consistent data access
    const tripType = getTempDataValue('tripType') as string;
    const startDate = getTempDataValue('startDate') as string;
    const endDate = getTempDataValue('endDate') as string;
    const travelDate = getTempDataValue('travelDate') as string;
    
    // Also try alternative key names for backward compatibility
    const effectiveDate = getTempDataValue('effectiveDate') as string;
    const expiryDate = getTempDataValue('expiryDate') as string;
    
    // Debug: Log all available tempData (matching SummaryStep logging pattern)
    console.log('usePrincipalPolicyValidation: Debug tempData access via getTempDataValue:', {
      tripType,
      startDate,
      endDate,
      travelDate,
      effectiveDate,
      expiryDate
    });

    console.log('usePrincipalPolicyValidation: Direct tempData access:', tempData);
    
    // Enhanced data extraction following SummaryStep pattern
    let actualStartDate: string | null = startDate;
    let actualEndDate: string | null = endDate;
    let actualTravelDate: string | null = travelDate;
    let actualTripType = tripType || 'Single Trip';
    
    // Clean and validate the extracted dates (matching SummaryStep pattern)
    if (actualStartDate && actualStartDate.trim() !== '') {
      actualStartDate = actualStartDate.trim();
    } else {
      actualStartDate = effectiveDate && effectiveDate.trim() !== '' ? effectiveDate.trim() : null;
    }
    
    if (actualEndDate && actualEndDate.trim() !== '') {
      actualEndDate = actualEndDate.trim();
    } else {
      actualEndDate = expiryDate && expiryDate.trim() !== '' ? expiryDate.trim() : null;
    }
    
    if (actualTravelDate && actualTravelDate.trim() !== '') {
      actualTravelDate = actualTravelDate.trim();
    } else {
      actualTravelDate = null;
    }
    
    // Additional fallback extraction from tempData object (preserved for backward compatibility)
    if (tempData && typeof tempData === 'object') {
      const tempDataObj = tempData as Record<string, unknown>;
      
      // Check if data is nested under travelFormData (common pattern)
      const travelFormData = tempDataObj.travelFormData as Record<string, unknown> | undefined;
      
      console.log('usePrincipalPolicyValidation: Checking nested travelFormData:', {
        hasTravelFormData: !!travelFormData,
        travelFormDataKeys: travelFormData ? Object.keys(travelFormData) : [],
        directTempDataKeys: Object.keys(tempDataObj)
      });
      
      // Only use tempData object fallback if primary methods failed
      if (!actualStartDate) {
        // First try nested travelFormData, then direct tempData
        actualStartDate = (travelFormData?.startDate as string) ||
                         (travelFormData?.effectiveDate as string) ||
                         (tempDataObj.startDate as string) ||
                         (tempDataObj.effectiveDate as string) ||
                         (tempDataObj.start_date as string) || null;
      }
                      
      if (!actualEndDate) {
        // First try nested travelFormData, then direct tempData
        actualEndDate = (travelFormData?.endDate as string) ||
                       (travelFormData?.expiryDate as string) ||
                       (tempDataObj.endDate as string) ||
                       (tempDataObj.expiryDate as string) ||
                       (tempDataObj.end_date as string) || null;
      }
                     
      if (!actualTravelDate) {
        // First try nested travelFormData, then direct tempData
        actualTravelDate = (travelFormData?.travelDate as string) ||
                          (tempDataObj.travelDate as string) ||
                          (tempDataObj.travel_date as string) || null;
      }
                        
      if (!actualTripType || actualTripType === 'Single Trip') {
        // First try nested travelFormData, then direct tempData
        actualTripType = (travelFormData?.tripType as string) ||
                        (tempDataObj.tripType as string) ||
                        (tempDataObj.trip_type as string) ||
                        'Single Trip';
      }
      
      console.log('usePrincipalPolicyValidation: Extracted from tempData object (with nested support):', {
        actualStartDate,
        actualEndDate,
        actualTravelDate,
        actualTripType,
        source: travelFormData ? 'nested travelFormData' : 'direct tempData',
        tempDataKeys: Object.keys(tempDataObj),
        travelFormDataKeys: travelFormData ? Object.keys(travelFormData) : []
      });
    }

    console.log('usePrincipalPolicyValidation: Final resolved values:', {
      actualTripType,
      actualStartDate,
      actualEndDate,
      actualTravelDate
    });

    if (actualTripType === 'Annual Multi-Trip') {
      // For multi-trip plans, use travel date as start and calculate 90 days coverage
      const finalTravelDate = actualTravelDate || actualStartDate;
      
      if (!finalTravelDate) {
        console.error('usePrincipalPolicyValidation: No travel date found for multi-trip plan');
        throw new Error('Travel date is required for multi-trip plans');
      }
      
      const coverageStart = finalTravelDate;
      const coverageEnd = calculateAnnualMaxCoverageDate(finalTravelDate);
      
      if (!coverageEnd) {
        throw new Error('Failed to calculate coverage end date for multi-trip plan');
      }
      
      console.log('usePrincipalPolicyValidation: Multi-trip coverage dates:', {
        coverageStart,
        coverageEnd
      });
      
      return {
        coverage_start: coverageStart,
        coverage_end: coverageEnd
      };
    } else {
      // For single trip plans, use start and end dates directly
      if (!actualStartDate || !actualEndDate) {
        console.error('usePrincipalPolicyValidation: Missing dates for single trip plan:', {
          actualStartDate,
          actualEndDate,
          tempDataAvailable: !!tempData,
          tempDataType: typeof tempData,
          tempDataKeys: tempData && typeof tempData === 'object' ? Object.keys(tempData as Record<string, unknown>) : []
        });
        throw new Error('Start date and end date are required for single trip plans');
      }
      
      console.log('usePrincipalPolicyValidation: Single trip coverage dates:', {
        coverage_start: actualStartDate,
        coverage_end: actualEndDate
      });
      
      return {
        coverage_start: actualStartDate,
        coverage_end: actualEndDate
      };
    }
  }, [getTempDataValue, tempData]);

  // Main validation function
  const validatePrincipalPolicy = useCallback(async (
    principalData: PrincipalInsuredData
  ): Promise<PrincipalPolicyValidationResponse> => {
    console.log('🚀 usePrincipalPolicyValidation: validatePrincipalPolicy called with:', principalData);
    setLoading(true);
    setError(null);

    try {
      // Validate required inputs
      if (!principalData) {
        console.error('❌ usePrincipalPolicyValidation: Principal insured data is required');
        throw new Error('Principal insured data is required');
      }

      console.log('✅ usePrincipalPolicyValidation: Principal data validation passed');

      const requiredFields = ['firstName', 'lastName', 'dateOfBirth', 'gender', 'idType', 'idNumber'];
      for (const field of requiredFields) {
        if (!principalData[field as keyof PrincipalInsuredData]?.trim()) {
          console.error(`❌ usePrincipalPolicyValidation: Required field missing: ${field}`);
          throw new Error(`${field} is required`);
        }
      }

      console.log('✅ usePrincipalPolicyValidation: All required fields validation passed');

      // Calculate coverage dates based on travel information
      console.log('🔄 usePrincipalPolicyValidation: About to calculate coverage dates...');
      const coverageDates = calculateCoverageDates();
      console.log('✅ usePrincipalPolicyValidation: Coverage dates calculated successfully:', coverageDates);

      // Prepare request body matching the API specification
      const requestBody: PrincipalPolicyValidationRequest = {
        firstName: principalData.firstName.trim(),
        middleName: principalData.middleName?.trim() || '',
        lastName: principalData.lastName.trim(),
        suffix: principalData.suffix?.trim() || '',
        birthdate: formatDateToYYYYMMDD(principalData.dateOfBirth),
        gender: principalData.gender,
        idType: principalData.idType,
        idNumber: principalData.idNumber.trim(),
        coverage_start: formatDateToYYYYMMDD(coverageDates.coverage_start),
        coverage_end: formatDateToYYYYMMDD(coverageDates.coverage_end),
        dataId: tempDataId ||""
      };

      const utmQsParams = getUtmQsParams(queryParams);
      
      // Make API call using enhancedApi
      const data: PrincipalPolicyValidationResponse = await enhancedApi.post(
        '/api/validation/principal-existing-policy' + utmQsParams,
        requestBody,
        {
          timeout: 10000, // 10 second timeout
          headers: {
            'Content-Type': 'application/json',
          }
        },
        1 // High priority for validation
      );

      console.log('usePrincipalPolicyValidation: Received response:', data);

      // Always return the response - let the component handle success/failure logic
      console.log('usePrincipalPolicyValidation: Validation completed:', {
        success: data.success,
        coverage_end: data.coverage_end,
        message: data.message
      });
      
      return data;

    } catch (err) {
      console.error('usePrincipalPolicyValidation: Error validating principal policy:', err);
      
      let errorMessage = 'Failed to validate principal policy';
      
      if (axios.isAxiosError(err)) {
        if (err.response) {
          // Server responded with error status
          const serverMessage = err.response.data?.message || err.response.statusText;
          errorMessage = `Server error (${err.response.status}): ${serverMessage}`;
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
        } else {
          // Something else happened
          errorMessage = `Request error: ${err.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Return error response - preserve original success value for proper error handling
      return {
        success: false, // Keep false to indicate validation failed
        coverage_end: null,
        coverage_start: null,
        message: errorMessage,
        code: axios.isAxiosError(err) && err.response?.status ? err.response.status : 500
      };

    } finally {
      setLoading(false);
    }
  }, [calculateCoverageDates]);

  // Clear all state
  const clearState = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return {
    validatePrincipalPolicy,
    loading,
    error,
    clearState
  };
};